/**
 * The top bar's call-to-action per page (prototype `HeaderView.actionsFor`).
 *
 * Module pages draw no `Export`: the prototype's button opened a dialog whose
 * `Confirm` produced nothing, and no export exists yet, so it is hidden until
 * one does.
 */
import { Button, Icon } from '@deepdive-ui';
import type { ReactNode } from 'react';

import type { TopBarKind } from '@/app/router/registry';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { cancelSettings, openDialog, requestSettingsSave } from '@/store/slices/uiSlice';

export function useTopBarActions(kind: TopBarKind): ReactNode {
  const dispatch = useAppDispatch();
  const settingsDirty = useAppSelector((state) => state.ui.settings.dirty);

  switch (kind) {
    case 'reports-list':
      return (
        <Button
          hierarchy="primary"
          iconLeading={<Icon name="add" />}
          onClick={() => dispatch(openDialog('create-report'))}
          aria-haspopup="dialog"
        >
          Create a report
        </Button>
      );
    case 'deepdive-home':
      return (
        <Button
          hierarchy="tertiary"
          href="/deepdive-ai/history"
          iconLeading={<Icon name="chatHistory" />}
        >
          Chat history
        </Button>
      );
    case 'deepdive-chat':
      return (
        <>
          <Button
            hierarchy="tertiary"
            href="/deepdive-ai/history"
            iconLeading={<Icon name="chatHistory" />}
          >
            Chat history
          </Button>
          <Button hierarchy="secondary" href="/deepdive-ai">
            New chat
          </Button>
        </>
      );
    case 'settings':
      return (
        <>
          <Button
            hierarchy="tertiary"
            disabled={!settingsDirty}
            onClick={() => dispatch(cancelSettings())}
          >
            Cancel
          </Button>
          <Button
            hierarchy="primary"
            disabled={!settingsDirty}
            onClick={() => dispatch(requestSettingsSave())}
          >
            Save changes
          </Button>
        </>
      );
    case 'module':
    case 'report-detail':
    case 'deepdive-history':
    case 'quick-search':
    case 'influencer-detail':
      return undefined;
  }
}
